import { Injectable } from '@angular/core';
import { BaseApiService } from './base-api.service';
import { AuditLogRequest, AuditLogRow, AuditLogType } from '../../types/generated.types';
import { ApiConversionHelper } from '../../helpers/api-conversion.helper';
import { DateHelper } from '../../helpers/date.helper';
import { AuditLogPropertyType } from '../../enums/generated.enums';
import { SchedulerTaskConfiguration } from '../../types/scheduler/scheduler-task.type';
import { RecoveryConfiguration } from '../../types/scheduler/recovery-configuration.type';
import { LazyLoadingData } from 'src/app/shared/components/mat-table/lazy-loaded.data-source';

@Injectable({ providedIn: 'root' })
export class AuditLogApiService extends BaseApiService {
  protected get baseUrl(): string {
    return `${super.baseUrl}/audit-log`;
  }

  async getAuditLogTypes(): Promise<AuditLogType[]> {
    return await this.get<AuditLogType[]>('types');
  }

  async getAuditLog(request: AuditLogRequest): Promise<LazyLoadingData<AuditLogRow>> {
    const requestCopy = { ...request };
    ApiConversionHelper.convertDateTimesForApi(requestCopy, ['from', 'to']);
    const result = await this.get<LazyLoadingData<AuditLogRow>>('', requestCopy);
    const rows = result.data;
    ApiConversionHelper.convertDates(rows, ['timestamp']);
    rows.forEach(r => {
      if (r.propertyType === AuditLogPropertyType.Time) {
        ApiConversionHelper.convertTimeSpans(r, ['oldValue', 'newValue']);
      }
      if (r.propertyType === AuditLogPropertyType.TimeSpan) {
        ApiConversionHelper.convertTimeSpans(r, ['oldValue', 'newValue']);
      }
      if (r.propertyType === AuditLogPropertyType.DateTime) {
        ApiConversionHelper.convertDates(r, ['oldValue', 'newValue']);
      }
      if (r.propertyType === AuditLogPropertyType.TaskConfiguration) {
        ApiConversionHelper.convertSchedulerTaskConfiguration(r.oldValue as SchedulerTaskConfiguration);
        ApiConversionHelper.convertSchedulerTaskConfiguration(r.newValue as SchedulerTaskConfiguration);
      }
      if (r.propertyType === AuditLogPropertyType.RecoveryConfiguration) {
        ApiConversionHelper.convertRecoveryConfiguration(r.oldValue as RecoveryConfiguration);
        ApiConversionHelper.convertRecoveryConfiguration(r.newValue as RecoveryConfiguration);
      }
      if (r.propertyType === AuditLogPropertyType.CalendarEvent) {
        ApiConversionHelper.convertDates(r.oldValue, ['start', 'end']);
        ApiConversionHelper.convertDates(r.newValue, ['start', 'end']);
      }
    });

    return result;
  }

  async getUserEmails(tenantId: string, from: Date, to: Date): Promise<string[]> {
    return await this.get<string[]>(`user-emails/${DateHelper.formatApiDateTime(from)}/${DateHelper.formatApiDateTime(to)}${this.getTenantParameter(tenantId)}`);
  }

  async getEntityNames(tenantId: string, from: Date, to: Date): Promise<string[]> {
    return await this.get<string[]>(`entity-names/${DateHelper.formatApiDateTime(from)}/${DateHelper.formatApiDateTime(to)}${this.getTenantParameter(tenantId)}`);
  }
}
